import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { crmTerritorialAPI } from '../../services/api';
import { Loader2 } from 'lucide-react';

// P3K — Reasignación masiva auditada de cartera: previsualización + lote atómico.
export const ReasignacionMasiva = ({ prospectos, asesores, onCerrar, onHecho }) => {
  const { toast } = useToast();
  const [destino, setDestino] = useState('');
  const [motivo, setMotivo] = useState('');
  const [incluirCliente, setIncluirCliente] = useState(true);
  const [preview, setPreview] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const seccionesSel = [...new Set(prospectos.map((p) => p.seccion_id))];
  const disponibles = (seccionesSel.length === 1
    ? asesores.filter((a) => a.seccion_id === seccionesSel[0]) : asesores)
    .filter((a) => !prospectos.every((p) => p.asesor?.id === a.asesor_id));
  const listo = destino && motivo.trim().length >= 5;

  const llamar = async (dryRun) => {
    setEnviando(true);
    try {
      const r = await crmTerritorialAPI.reasignarMasivo({
        oportunidad_ids: prospectos.map((p) => p.oportunidad_id),
        asesor_destino_id: destino,
        motivo: motivo.trim(),
        incluir_cliente: incluirCliente,
        dry_run: dryRun,
      });
      if (dryRun) { setPreview(r); return; }
      toast({ title: 'Cartera reasignada',
        description: `${r.reasignadas} prospecto(s) ahora con ${r.asesor_destino.nombre}. ` +
          `Clientes trasladados: ${r.clientes_trasladados}.` });
      onHecho();
      onCerrar();
    } catch (e) {
      toast({ title: 'Reasignación rechazada', description: e.message, variant: 'destructive' });
    } finally { setEnviando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl" data-testid="reasignacion-masiva-dialog">
        <DialogHeader>
          <DialogTitle>Reasignación masiva de cartera ({prospectos.length})</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select value={destino}
                    onChange={(e) => { setDestino(e.target.value); setPreview(null); }}
                    className="border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="masiva-asesor-destino">
              <option value="">Asesor destino…</option>
              {disponibles.map((a) => (
                <option key={a.asesor_id} value={a.asesor_id}>{a.nombre} · {a.seccion}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={incluirCliente}
                     onChange={(e) => { setIncluirCliente(e.target.checked); setPreview(null); }}
                     data-testid="masiva-incluir-cliente" />
              Trasladar también el cliente
            </label>
          </div>
          <Input value={motivo} placeholder="Motivo de la reasignación (mínimo 5 caracteres)"
                 onChange={(e) => { setMotivo(e.target.value); setPreview(null); }}
                 data-testid="masiva-motivo" />

          {preview && (
            <div className="border rounded-lg p-3 space-y-2" data-testid="masiva-preview">
              <div className="flex gap-2 text-sm">
                <Badge className="bg-emerald-100 text-emerald-800"
                       data-testid="masiva-preview-aptas">aptas: {preview.aptas}</Badge>
                <Badge className={preview.rechazadas
                  ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}
                       data-testid="masiva-preview-rechazadas">
                  rechazadas: {preview.rechazadas}
                </Badge>
              </div>
              <div className="max-h-48 overflow-y-auto text-sm space-y-1">
                {preview.evaluacion.map((e) => (
                  <div key={e.oportunidad_id} className="flex justify-between gap-2">
                    <span className="truncate">{e.nombre || e.oportunidad_id}</span>
                    {e.apta
                      ? <span className="text-emerald-600 shrink-0">apta</span>
                      : <span className="text-red-600 shrink-0">{e.motivo_rechazo}</span>}
                  </div>
                ))}
              </div>
              {preview.rechazadas > 0 && (
                <p className="text-xs text-red-600">
                  El lote es atómico: quita de la selección las no aptas para poder ejecutar.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => llamar(true)} disabled={!listo || enviando}
                    data-testid="masiva-previsualizar-btn">
              {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Previsualizar
            </Button>
            <Button onClick={() => llamar(false)}
                    disabled={!listo || enviando || !preview || preview.rechazadas > 0}
                    data-testid="masiva-confirmar-btn">
              {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar reasignación
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Lote todo-o-nada dentro de la misma sección: primero previsualiza, luego confirma.
            Cada movimiento queda auditado con asesor anterior y nuevo, se trasladan las
            actividades pendientes y se anota en la bitácora. Sin correos ni avisos externos.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReasignacionMasiva;
